import {
  createApi,
  fetchBaseQuery,
  type BaseQueryFn,
  type FetchArgs,
  type FetchBaseQueryError,
} from '@reduxjs/toolkit/query/react';
import type { RootState } from '@shared/redux/root-state';
import queryString from 'query-string';
import { refreshToken } from '@shared/utils/refresh-token';
import { waitRehydration } from '@shared/utils/wait-rehydration';
import { cloneFetchArgs } from '@shared/utils/clone-fetch-args';

const rawBase = fetchBaseQuery({
  baseUrl: '/api',
  prepareHeaders: (headers, api) => {
    const { access_token: accessToken } = (api.getState() as RootState).auth.authInfo || {};
    const language = (api.getState() as RootState).appSettings.language || 'en';
    if (accessToken) headers.set('Authorization', `Bearer ${accessToken}`);
    headers.set('X-Language', language);
    return headers;
  },
  paramsSerializer: (params) => queryString.stringify(params, { arrayFormat: 'bracket' }),
});

let refreshGate: Promise<boolean> | null = null;

export const baseQueryWithReauth: BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError
> = async (args, api, extra) => {
  await waitRehydration();

  let result = await rawBase(args, api, extra);

  const status = (result as any)?.error?.status;
  const isAuthError = status === 401 || status === 403;

  if (!isAuthError) return result;

  if (!refreshGate) {
    refreshGate = refreshToken().finally(() => {
      setTimeout(() => (refreshGate = null), 0);
    });
  }
  const ok = await refreshGate;

  if (!ok) {
    return result;
  }

  const retryArgs = cloneFetchArgs(args);
  result = await rawBase(retryArgs, api, extra);

  return result;
};

export const api = createApi({
  baseQuery: baseQueryWithReauth,
  tagTypes: ['api', 'auth'],
  reducerPath: 'api',
  endpoints: () => ({}),
});
